import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ChevronRight from "@mui/icons-material/ChevronRight";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import { ScoutHost } from "generated-graphql/graphql";
import { KeyboardEvent, type ReactNode, useCallback, useState } from "react";

import type { ScoutTodoItem, ScoutTodoSnapshot } from "./scoutTodoSnapshot";

import { formatTodoItemLabel, formatTodoItemResult } from "./customerFacingCopy";
import { useScoutHostContext } from "./ScoutHostContext";

const MAX_RESULT_CHARS = 120;

export type ScoutTodoListVariant = "full" | "short";

function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1)}…`;
}

function formatSummary(snapshot: ScoutTodoSnapshot): string {
  const { summary } = snapshot;
  const completed = summary.done + summary.skipped;
  if (summary.total === 0) {
    return snapshot.isPlanning ? "Planning steps…" : "No steps yet";
  }
  if (completed === summary.total) {
    return `${summary.total} step${summary.total === 1 ? "" : "s"} complete`;
  }
  if (summary.in_progress > 0) {
    return `${completed} of ${summary.total} complete · ${summary.in_progress} in progress`;
  }
  if (summary.interrupted > 0) {
    return `${completed} of ${summary.total} complete · ${summary.interrupted} interrupted`;
  }
  const remaining = summary.pending;
  return `${completed} of ${summary.total} complete · ${remaining} remaining`;
}

/**
 * Compact progress mark used on `xs` in the full checklist and always in the
 * popover short status: an `n of m` chip, or the green check once every step
 * is done. Full summary text stays in `aria-label` for assistive tech.
 */
function TodoCompactSummary({
  snapshot,
  summaryText,
}: {
  snapshot: ScoutTodoSnapshot;
  summaryText: string;
}): ReactNode {
  const theme = useTheme();
  const completedCount = snapshot.summary.done + snapshot.summary.skipped;
  const totalCount = snapshot.summary.total;
  const allStepsComplete = totalCount > 0 && completedCount === totalCount;

  if (allStepsComplete) {
    return (
      <CheckCircleOutline
        fontSize="small"
        aria-label={summaryText}
        sx={{ color: theme.palette.success.main }}
      />
    );
  }
  if (totalCount > 0) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="info"
        label={`${completedCount} of ${totalCount}`}
        aria-label={summaryText}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      />
    );
  }
  return (
    <Typography variant="caption" color="text.secondary" noWrap>
      {summaryText}
    </Typography>
  );
}

/**
 * Full-checklist header progress: sentence on `sm+`, compact chip/check on `xs`.
 */
function TodoProgressSummary({
  snapshot,
  summaryText,
}: {
  snapshot: ScoutTodoSnapshot;
  summaryText: string;
}) {
  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          flexShrink: 0,
          fontVariantNumeric: "tabular-nums",
          display: { xs: "none", sm: "block" },
        }}
      >
        {summaryText}
      </Typography>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <TodoCompactSummary snapshot={snapshot} summaryText={summaryText} />
      </Box>
    </>
  );
}

function TodoStatusIcon({ status }: { status: ScoutTodoItem["status"] }) {
  const theme = useTheme();

  if (status === "in_progress") {
    return (
      <CircularProgress
        size={16}
        thickness={5}
        aria-hidden
        sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
      />
    );
  }
  if (status === "done") {
    return (
      <CheckCircleOutline
        fontSize="small"
        aria-hidden
        sx={{ color: theme.palette.success.main, flexShrink: 0 }}
      />
    );
  }
  if (status === "skipped") {
    return (
      <RemoveCircleOutline
        fontSize="small"
        aria-hidden
        sx={{ color: "text.disabled", flexShrink: 0 }}
      />
    );
  }
  if (status === "interrupted") {
    return (
      <PauseCircleOutline
        fontSize="small"
        aria-hidden
        sx={{ color: theme.palette.warning.main, flexShrink: 0 }}
      />
    );
  }
  return (
    <RadioButtonUnchecked
      fontSize="small"
      aria-hidden
      sx={{ color: "text.disabled", flexShrink: 0 }}
    />
  );
}

function TodoRow({ item }: { item: ScoutTodoItem }) {
  const theme = useTheme();
  const isActive = item.status === "in_progress";
  const isComplete = item.status === "done";
  const isSkipped = item.status === "skipped";
  const isInterrupted = item.status === "interrupted";
  const displayLabel = formatTodoItemLabel(item);
  const displayResult = formatTodoItemResult(item);

  const labelColor = (() => {
    if (isActive) return "text.primary";
    if (isComplete || isSkipped || isInterrupted) return "text.secondary";
    return "text.primary";
  })();

  let rowBg = "transparent";
  if (isActive) {
    rowBg = alpha(theme.palette.primary.main, 0.06);
  } else if (isInterrupted) {
    rowBg = alpha(theme.palette.warning.main, 0.06);
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: rowBg,
      }}
    >
      <Box sx={{ mt: 0.125 }}>
        <TodoStatusIcon status={item.status} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: labelColor,
            fontWeight: isActive ? 500 : 400,
            textDecoration: isSkipped ? "line-through" : "none",
          }}
        >
          {displayLabel}
        </Typography>
        {displayResult && (isComplete || isSkipped) ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {truncateText(displayResult, MAX_RESULT_CHARS)}
          </Typography>
        ) : null}
        {isSkipped && !displayResult ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            Skipped
          </Typography>
        ) : null}
        {isInterrupted ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            Interrupted
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function checklistShellSx(theme: Theme) {
  return {
    mt: 1,
    mb: 1,
    px: 1.25,
    py: 1,
    minWidth: 0,
    // Fixed width on sm+ so expanding/collapsing only animates height, not
    // width (the box no longer shrinks to its content). Falls back to full
    // width on mobile, and maxWidth guards against overflowing a narrower
    // container such as the Scout popover.
    width: { xs: "100%", sm: 520 },
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: 1.5,
    border: 1,
    borderColor: theme.palette.action.active,
    bgcolor: alpha(theme.palette.background.paper, 0.6),
  } as const;
}

function ChecklistBody({ snapshot }: { snapshot: ScoutTodoSnapshot }) {
  if (snapshot.isPlanning) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, px: 1 }}>
        <CircularProgress size={14} thickness={5} aria-hidden />
        <Typography variant="body2" color="text.secondary">
          Planning the steps…
        </Typography>
      </Stack>
    );
  }
  if (snapshot.items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
        Waiting for the checklist…
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25} aria-live="polite" aria-atomic="false">
      {snapshot.items.map((item) => (
        <TodoRow key={item.id} item={item} />
      ))}
    </Stack>
  );
}

export function ScoutTodoList({
  snapshot,
  isStreaming = false,
  variant,
}: {
  snapshot: ScoutTodoSnapshot;
  isStreaming?: boolean;
  /**
   * Override density. Defaults from Scout host: popover keeps the compact chip
   * in the header (the long summary overflows); full-page uses responsive
   * summary text. Both remain expandable.
   */
  variant?: ScoutTodoListVariant;
}) {
  const theme = useTheme();
  const { host } = useScoutHostContext();
  const density = variant ?? (host === ScoutHost.Popover ? "short" : "full");
  const useCompactSummary = density === "short";
  // Popover starts collapsed so the chip fits; full-page starts expanded.
  const [expanded, setExpanded] = useState(!useCompactSummary);

  const toggleExpanded = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  const handleHeaderKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded]
  );

  if (!snapshot.hasChecklist) {
    return null;
  }

  const summaryText = formatSummary(snapshot);
  const isWorking = snapshot.isPlanning || (isStreaming && snapshot.summary.in_progress > 0);
  const showCollapsedWorkingSpinner = !expanded && isWorking;

  return (
    <Box
      role="region"
      aria-label="Scout checklist"
      aria-busy={snapshot.isPlanning || isStreaming}
      sx={checklistShellSx(theme)}
    >
      <Stack spacing={0.75}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label="Toggle Scout checklist"
          onClick={toggleExpanded}
          onKeyDown={handleHeaderKeyDown}
          sx={{
            minHeight: 28,
            minWidth: 0,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: 1,
            mx: -0.5,
            px: 0.5,
            "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
          }}
        >
          <IconButton
            size="small"
            aria-hidden
            tabIndex={-1}
            sx={{
              p: 0.25,
              flexShrink: 0,
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 500, color: "text.primary", minWidth: 0, lineHeight: 1 }}
            >
              Checklist
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ flexShrink: 0, justifyContent: "flex-end" }}
            >
              {showCollapsedWorkingSpinner ? (
                <CircularProgress
                  size={14}
                  thickness={5}
                  aria-hidden
                  sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
                />
              ) : null}
              {useCompactSummary ? (
                <TodoCompactSummary snapshot={snapshot} summaryText={summaryText} />
              ) : (
                <TodoProgressSummary snapshot={snapshot} summaryText={summaryText} />
              )}
            </Stack>
          </Stack>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ChecklistBody snapshot={snapshot} />
        </Collapse>
      </Stack>
    </Box>
  );
}
